import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { FeatherIcon } from "component/shared/feather-icon";
import { RefreshTable } from "component/shared/refresh-table";
import { Sort } from "component/shared/table";
import { Office } from "model/office";
import { User } from "model/user";
import { deleteOffice } from "redux/slice/offices";
import { RootState } from "redux/store";
import { OfficeColumnType } from "type/office-column";
import { AddOfficeDialog } from "../office-dialog/add-office-dialog";
import { DeleteOfficeDialog } from "./delete-dialog";
import { styles } from "./style";
import { urls } from "routes/urls";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import MuiAlert from "@mui/material/Alert";

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
	// Priority is no longer a column here — it moves to Teams & Offices > General as the
	// "Display Order" setting under MAX-4065. BrokerOffices.Priority still holds the values.
	let columnTypes = [];
	columnTypes.push(OfficeColumnType.OFFICE_NAME);
	columnTypes.push(OfficeColumnType.ADDRESS);
	columnTypes.push(OfficeColumnType.CITY);
	columnTypes.push(OfficeColumnType.ACTION);
	return {
		columnTypes,
	};
};

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			deleteOffice,
		},
		dispatch
	);

interface Props
	extends OwnProps,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps>,
		WithStyles<typeof styles> {}

interface OwnProps {
	offices: Office[];
	user: User;
	searchTerm: string;
	onSearchChange: (term: string) => void;
}

interface State {
	addOfficeDialogIsOpen: boolean;
	deleteOfficeDialogIsOpen: boolean;
	officeToEdit: Office | null;
	officeToDelete: Office | null;
}

class Component extends React.Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		this.state = {
			addOfficeDialogIsOpen: false,
			deleteOfficeDialogIsOpen: false,
			officeToDelete: null,
			officeToEdit: null,
		};
	}

	private showAddOfficeDialog = () =>
		this.setState({ addOfficeDialogIsOpen: true, officeToEdit: null });

	private hideAddOfficeDialog = () =>
		this.setState({ addOfficeDialogIsOpen: false, officeToEdit: null });

	private showDeleteOfficeDialog = (office: Office) => {
		this.setState({
			officeToDelete: office,
			deleteOfficeDialogIsOpen: true,
		});
	};

	private hideDeleteOfficeDialog = () =>
		this.setState({
			officeToDelete: null,
			deleteOfficeDialogIsOpen: false,
		});

	private deleteOffice = () => {
		const { officeToDelete } = this.state;
		if (officeToDelete) {
			this.props.deleteOffice({ office: {
				...officeToDelete,
			}});
		}
		this.hideDeleteOfficeDialog();
	};

	public render() {
		const { offices, classes = {}, user, searchTerm, onSearchChange } = this.props;
		const {
			addOfficeDialogIsOpen,
			deleteOfficeDialogIsOpen,
			officeToDelete,
		} = this.state;
		const hasMultipleOffices = user.permissions.multipleOffices;
		const canAddOffice = hasMultipleOffices || offices.length === 0;
		// RefreshTable sorts through this; the list defaults to office name ascending.
		const sort: Sort = { id: "officeName", desc: false };

		return (
			<>
				<AddOfficeDialog
					open={addOfficeDialogIsOpen}
					onClose={this.hideAddOfficeDialog}
				/>
				{officeToDelete && (
					<DeleteOfficeDialog
						deleteOffice={this.deleteOffice}
						deleteOfficeDialogIsOpen={deleteOfficeDialogIsOpen}
						hideDeleteOfficeDialog={this.hideDeleteOfficeDialog}
						office={officeToDelete}
					/>
				)}
				<Mui.Grid container className={classes.toolbar} alignItems="center" spacing={2}>
					<Mui.Grid item xs={12} sm>
						<Mui.TextField
							className={classes.searchInput}
							placeholder="Search offices"
							onChange={(event) => onSearchChange(event.target.value)}
							value={searchTerm}
							variant="outlined"
							InputProps={{
								startAdornment: (
									<Mui.InputAdornment position="start">
										<Icons.Search size={20} />
									</Mui.InputAdornment>
								),
							}}
						/>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Button
							className={classes.actionButton}
							variant="contained"
							color="primary"
							startIcon={<Icons.Plus size={18} />}
							onClick={() => this.showAddOfficeDialog()}
							disabled={!canAddOffice}
						>
							Add Office
						</Mui.Button>
					</Mui.Grid>
					{!hasMultipleOffices && offices.length > 0 && (
						<Mui.Grid item xs={12}>
							<MuiAlert severity="warning">
								To add another office, please contact sales to upgrade.
							</MuiAlert>
						</Mui.Grid>
					)}
				</Mui.Grid>
				<RefreshTable
					items={offices}
					sort={sort}
					rowKey={(office) => office.id}
					userId={user.agentId}
					tableKey="offices"
					columns={[
						{
							id: "officeName",
							title: OfficeColumnType.OFFICE_NAME.label,
							sort: (office1, office2) => {
								return office1.name.toLowerCase() < office2.name.toLowerCase()
								? -1
								: 1;
							},
							value: (office) => {
								return (
									<Mui.Link
										className={classes.link}
										component={RouterLinkWrapper} to={urls.settingsOffice(office)}
									>
										{office.name}
									</Mui.Link>
								);
							},
						},
						{
							id: "address",
							title: OfficeColumnType.ADDRESS.label,
							sort: (office1, office2) => {
								return office1.address.toLowerCase() < office2.address.toLowerCase()
									? -1
									: 1;
							},
							value: (office) => {
								return office.address;
							},
						},
						{
							id: "city",
							title: OfficeColumnType.CITY.label,
							sort: (office1, office2) => {
								return office1.city.toLowerCase() < office2.city.toLowerCase()
									? -1
									: 1;
							},
							value: (office) => {
								return office.city;
							},
						},
						{
							id: "delete",
							width: 70,
							showOnHover: true,
							value: (office) => {
								return (
									<Mui.IconButton onClick={() => this.showDeleteOfficeDialog(office)}>
										<FeatherIcon>
											<Icons.Trash />
										</FeatherIcon>
									</Mui.IconButton>
								);
							},
						},
					]}
				/>
			</>
		);
	}
}

export const OfficeTable = withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles);
